import React, { useState, useMemo } from 'react';
import Card from '../../../../shared/components/Card';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';

export interface AllianceMemberRow {
  id: string;
  name: string;
  allianceRoles: string[];
  poste: string;
  credits: number;
  online: boolean;
}

const ALLIANCE_ROLES = [
  'Admin',
  'Co-Admin',
  'Formateur',
  'Formateur de l\'alliance',
  'Membre',
  'Personnel de l\'alliance',
  'Finances',
  'Journal de modération',
  'Gestionnaire d\'événements',
];

const MOCK_MEMBERS: AllianceMemberRow[] = [
  { id: '1', name: 'Wiglaum', allianceRoles: ['Admin'], poste: 'Administrateur d\'alliance', credits: 21860151339, online: true },
  { id: '2', name: 'M0rgan', allianceRoles: ['Co-Admin', 'Formateur', 'Finances', 'Personnel de l\'alliance', 'Journal de modération', 'Gestionnaire d\'événements'], poste: 'Coadministrateur d\'alliance', credits: 15200321000, online: true },
  { id: '3', name: 'samourai57シ', allianceRoles: ['Membre'], poste: '', credits: 8900456000, online: false },
  { id: '4', name: 'Victor77', allianceRoles: ['Membre'], poste: '', credits: 6540321000, online: true },
  { id: '5', name: 'mappaz', allianceRoles: ['Membre'], poste: 'Formateur de l\'alliance', credits: 5210987000, online: false },
  { id: '6', name: 'Fr3d', allianceRoles: ['Membre', 'Formateur de l\'alliance', 'Personnel de l\'alliance'], poste: '', credits: 4100234500, online: true },
  { id: '7', name: 'kiliannb', allianceRoles: ['Membre', 'Formateur de l\'alliance', 'Personnel de l\'alliance'], poste: '', credits: 3890123400, online: false },
  { id: '8', name: 'Hugues53', allianceRoles: ['Admin'], poste: 'Administrateur d\'alliance', credits: 2560789012, online: true },
];

const PAGE_SIZE = 5;

const AllianceMembers: React.FC = () => {
  const { t } = usePageTranslation('game', 'AlliancePage');
  const [members, setMembers] = useState<AllianceMemberRow[]>(MOCK_MEMBERS);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editAllianceRoles, setEditAllianceRoles] = useState<string[]>([]);
  const [editPoste, setEditPoste] = useState('');

  const filteredMembers = useMemo(() => {
    let list = [...members];
    if (searchApplied.trim()) {
      const q = searchApplied.toLowerCase();
      list = list.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.allianceRoles.some((r) => r.toLowerCase().includes(q)) ||
          m.poste.toLowerCase().includes(q)
      );
    }
    if (onlineOnly) list = list.filter((m) => m.online);
    return list;
  }, [members, searchApplied, onlineOnly]);

  const totalPages = Math.max(1, Math.ceil(filteredMembers.length / PAGE_SIZE));
  const paginatedMembers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredMembers.slice(start, start + PAGE_SIZE);
  }, [filteredMembers, currentPage]);

  const handleSearch = () => {
    setSearchApplied(searchQuery);
    setCurrentPage(1);
  };

  const startEdit = (m: AllianceMemberRow) => {
    setEditingId(m.id);
    setEditAllianceRoles([...m.allianceRoles]);
    setEditPoste(m.poste);
  };

  const toggleEditRole = (role: string) => {
    setEditAllianceRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const saveEdit = () => {
    if (!editingId) return;
    setMembers((prev) =>
      prev.map((m) =>
        m.id === editingId
          ? { ...m, allianceRoles: editAllianceRoles, poste: editPoste }
          : m
      )
    );
    setEditingId(null);
  };

  const formatAmount = (n: number) =>
    n.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' €';

  return (
    <Card className="p-6">
      <h2 className="text-2xl font-bold text-white mb-6">{t('members.title')}</h2>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex flex-1 gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder={t('members.searchPlaceholder')}
            className="flex-1 min-w-0 px-4 py-2 rounded-lg bg-gray-800 border border-gray-600 text-white placeholder-gray-500 focus:ring-2 focus:ring-indigo-500"
          />
          <button
            type="button"
            onClick={handleSearch}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium whitespace-nowrap"
          >
            {t('members.search')}
          </button>
        </div>
        <label className="flex items-center gap-2 text-gray-300 cursor-pointer shrink-0">
          <input
            type="checkbox"
            checked={onlineOnly}
            onChange={(e) => {
              setOnlineOnly(e.target.checked);
              setCurrentPage(1);
            }}
            className="rounded bg-gray-800 border-gray-600 text-indigo-500 focus:ring-indigo-500"
          />
          <span className="text-sm">{t('members.onlineOnly')}</span>
        </label>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-700">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-gray-800/80 border-b border-gray-700">
              <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('members.colName')}</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('members.colAllianceRole')}</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('members.colPoste')}</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('members.colCredits')}</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-300 w-20" aria-label={t('members.editRoles')} />
            </tr>
          </thead>
          <tbody>
            {paginatedMembers.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  {t('members.noResults')}
                </td>
              </tr>
            ) : (
              paginatedMembers.map((member) => (
                <tr key={member.id} className="border-b border-gray-700/50 hover:bg-gray-800/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="relative flex shrink-0">
                        <span className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-gray-400">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                          </svg>
                        </span>
                        {member.online && (
                          <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-gray-900" />
                        )}
                      </span>
                      <span className="font-medium text-white">{member.name}</span>
                    </div>
                  </td>

                  {editingId === member.id ? (
                    <>
                      <td className="px-4 py-2">
                        <div className="flex flex-wrap gap-1.5 max-w-md">
                          {ALLIANCE_ROLES.map((r) => (
                            <label
                              key={r}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-700/80 cursor-pointer hover:bg-gray-600 text-sm text-gray-300"
                            >
                              <input
                                type="checkbox"
                                checked={editAllianceRoles.includes(r)}
                                onChange={() => toggleEditRole(r)}
                                className="rounded border-gray-500 text-indigo-500 focus:ring-indigo-500"
                              />
                              <span>{r}</span>
                            </label>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={editPoste}
                          onChange={(e) => setEditPoste(e.target.value)}
                          placeholder={t('members.colPoste')}
                          className="w-full min-w-[140px] px-3 py-1.5 rounded-lg bg-gray-800 border border-gray-600 text-white text-sm placeholder-gray-500 focus:ring-2 focus:ring-indigo-500"
                        />
                      </td>
                      <td className="px-4 py-3 text-gray-300 whitespace-nowrap">
                        {formatAmount(member.credits)}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={saveEdit}
                            className="p-1.5 rounded text-green-500 hover:bg-green-500/20"
                            title={t('members.save')}
                            aria-label={t('members.save')}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={cancelEdit}
                            className="p-1.5 rounded text-gray-400 hover:bg-gray-600"
                            title={t('members.cancel')}
                            aria-label={t('members.cancel')}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {member.allianceRoles.map((r) => (
                            <span
                              key={r}
                              className="text-xs text-gray-300 bg-gray-700/50 px-2 py-0.5 rounded"
                            >
                              {r}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-400">
                          {member.poste || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-300 whitespace-nowrap">
                        {formatAmount(member.credits)}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => startEdit(member)}
                          className="p-2 rounded-lg text-gray-400 hover:bg-gray-600 hover:text-white transition-colors"
                          title={t('members.editRoles')}
                          aria-label={t('members.editRoles')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
          <button
            type="button"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1.5 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm"
          >
            {t('members.paginationPrev')}
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setCurrentPage(p)}
              className={`w-9 h-9 rounded text-sm font-medium ${
                p === currentPage
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-700 hover:bg-gray-600 text-white'
              }`}
            >
              {p}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm"
          >
            {t('members.paginationNext')}
          </button>
        </div>
      )}
    </Card>
  );
};

export default AllianceMembers;
